import { CellContext, Column, createColumnHelper } from '@tanstack/react-table';
import { CheckCircle, XCircle } from 'lucide-react';

import Podman from '@/assets/ico/vendor/podman.svg?c';
import Docker from '@/assets/ico/vendor/docker.svg?c';
import Kubernetes from '@/assets/ico/vendor/kubernetes.svg?c';
import { getValueAsArrayOfStrings } from '@/portainer/helpers/array';
import { pluralize } from '@/react/common/string-utils';

import { Badge, BadgeType } from '@@/Badge';
import { Link } from '@@/Link';
import { MultipleSelectionFilter } from '@@/datatables/Filter';
import { Icon } from '@@/Icon';
import { Tooltip } from '@@/Tip/Tooltip';
import { badgeTypeClasses } from '@@/Badge/Badge';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';
import { StackedStatusBar } from '@@/StackedStatusBar';

import {
  PolicyType,
  getPolicyTypeDisplayName,
  getPolicyCategory,
  getEnvironmentTypeDisplayName,
} from '../../types';

import { EnrichedPolicy, PolicyRollout } from './types';

const columnHelper = createColumnHelper<EnrichedPolicy>();

export const columns = [
  columnHelper.accessor('Name', {
    header: 'Name',
    cell: NameCell,
  }),
  columnHelper.accessor('EnvironmentType', {
    header: 'Environment',
    cell: EnvironmentCell,
    enableColumnFilter: true,
    filterFn: ({ original: policy }, columnId, filterValue: Array<string>) => {
      if (filterValue.length === 0) {
        return true;
      }

      const displayName = getEnvironmentTypeDisplayName(policy.EnvironmentType);
      return filterValue.includes(displayName);
    },
    meta: {
      filter: EnvironmentFilter,
    },
  }),
  columnHelper.accessor('Type', {
    header: 'Type',
    cell: TypeCell,
    enableColumnFilter: true,
    filterFn: ({ original: policy }, columnId, filterValue: Array<string>) => {
      if (filterValue.length === 0) {
        return true;
      }

      const displayName = getPolicyTypeDisplayName(policy.Type);
      return filterValue.includes(displayName);
    },
    meta: {
      filter: TypeFilter,
    },
  }),
  columnHelper.accessor(
    (row) => row.environmentGroups.map((group) => group.name),
    {
      id: 'attachedTo',
      header: 'Environment groups attached',
      cell: (props) => (
        <AttachedToCell
          environmentGroups={props.row.original.environmentGroups}
        />
      ),
    }
  ),
  columnHelper.accessor((row) => row.status, {
    id: 'status',
    header: 'Status',
    cell: StatusCell,
  }),
  // Last updated timestamp
  columnHelper.accessor('UpdatedAt', {
    header: 'Last updated',
    cell: LastUpdatedCell,
  }),
];

function NameCell({ getValue, row }: CellContext<EnrichedPolicy, string>) {
  const name = getValue();
  const policy = row.original;

  return (
    <Link
      to="portainer.policy"
      params={{ id: policy.Id }}
      data-cy={`policy-link-${name}`}
    >
      {name}
    </Link>
  );
}

function LastUpdatedCell({ getValue }: CellContext<EnrichedPolicy, string>) {
  const updatedAt = getValue();
  // UpdatedAt comes from the API as a string timestamp; fallback if missing
  if (!updatedAt) {
    return <span className="text-muted">Unknown</span>;
  }

  let formatted: string;
  try {
    const date = new Date(updatedAt);
    formatted = Number.isNaN(date.getTime())
      ? 'Unknown'
      : date.toLocaleString();
  } catch (e) {
    formatted = 'Unknown';
  }

  return <span>{formatted}</span>;
}

function EnvironmentCell({
  getValue,
}: CellContext<EnrichedPolicy, EnrichedPolicy['EnvironmentType']>) {
  const environmentType = getValue();

  function getIcon() {
    switch (environmentType) {
      case 'kubernetes':
        return Kubernetes;
      case 'podman':
        return Podman;
      case 'docker':
      case 'swarm':
      default:
        return Docker;
    }
  }

  const Icon = getIcon();
  const environmentDisplayName = getEnvironmentTypeDisplayName(environmentType);

  return (
    <div className="flex items-center">
      <Icon className="h-4 w-4" />
      <span>{environmentDisplayName}</span>
    </div>
  );
}

function TypeCell({
  row: { original: policy },
}: CellContext<EnrichedPolicy, PolicyType>) {
  const category = getPolicyCategory(policy.Type);
  const displayName = getPolicyTypeDisplayName(policy.Type);

  const badgeType: BadgeType = category === 'rbac' ? 'success' : 'info';

  return <Badge type={badgeType}>{displayName}</Badge>;
}

function AttachedToCell({
  environmentGroups,
}: {
  environmentGroups: EnrichedPolicy['environmentGroups'];
}) {
  const maxBadges = 5;
  const shortenedBadgeValues = environmentGroups.slice(0, maxBadges);
  const extraBadges = environmentGroups.length - maxBadges;

  return (
    <div className="flex flex-wrap max-w-md gap-x-2 gap-y-1">
      {shortenedBadgeValues.map((group) => (
        <Badge
          type={group.status}
          key={group.name}
          className="flex items-center gap-1 min-w-max"
        >
          <Icon icon={group.status === 'info' ? CheckCircle : XCircle} />
          <Link
            to="portainer.groups.group"
            params={{ id: group.id }}
            data-cy={`environment-group-link-${group.name}`}
            className={badgeTypeClasses[group.status]}
          >
            {group.name}
          </Link>
          {group.statusMessage && (
            <Tooltip message={group.statusMessage} className="!ml-0" />
          )}
        </Badge>
      ))}
      {extraBadges > 0 && (
        <Badge type="muted" className="flex items-center gap-1 min-w-max">
          + {extraBadges} more
        </Badge>
      )}
    </div>
  );
}

function formatRolloutTooltip(rollout: PolicyRollout): string {
  const parts: string[] = [];
  if (rollout.applied > 0) {
    parts.push(`${rollout.applied} active`);
  }
  if (rollout.pending > 0) {
    parts.push(`${rollout.pending} in progress`);
  }
  if (rollout.failed > 0) {
    parts.push(`${rollout.failed} failed`);
  }
  if (rollout.notSupported > 0) {
    parts.push(`${rollout.notSupported} not supported`);
  }
  return parts.join(', ');
}

function StatusCell({
  row,
}: CellContext<EnrichedPolicy, EnrichedPolicy['status']>) {
  const policy = row.original;
  const { rollout } = policy;

  const tooltipMessage = formatRolloutTooltip(rollout);

  const displayMessage =
    rollout.total === 0
      ? 'No environments targeted'
      : `${rollout.total} ${pluralize(rollout.total, 'environment')}${
          tooltipMessage ? `: ${tooltipMessage}` : ''
        }`;

  if (rollout.total === 0) {
    return <span className="text-muted">No environments targeted</span>;
  }

  return (
    <TooltipWithChildren message={displayMessage}>
      <span>
        <Link
          to="portainer.policy"
          params={{ id: policy.Id }}
          className="no-link"
          data-cy={`policy-status-link-${policy.Id}`}
          aria-label={displayMessage}
        >
          <StackedStatusBar
            className="!w-48"
            steps={[
              {
                value: rollout.applied,
                className: 'bg-success-7',
                label: 'Active',
              },
              {
                value: rollout.pending,
                className: 'bg-warning-7',
                label: 'In progress',
              },
              {
                value: rollout.failed,
                className: 'bg-error-7',
                label: 'Failed',
              },
              {
                value: rollout.notSupported,
                className: 'bg-gray-6',
                label: 'Not supported',
              },
            ]}
            total={rollout.total}
            aria-label="Policy rollout status"
          />
        </Link>
      </span>
    </TooltipWithChildren>
  );
}

const environmentFilterOptions = [
  'Kubernetes',
  'Docker',
  'Podman',
  'Docker Swarm',
] as const;

function EnvironmentFilter({
  column: { getFilterValue, setFilterValue, id },
}: {
  column: Column<EnrichedPolicy>;
}) {
  const value = getFilterValue();
  const valueAsArray = getValueAsArrayOfStrings(value);

  return (
    <MultipleSelectionFilter
      options={environmentFilterOptions}
      filterKey={id}
      value={valueAsArray}
      onChange={setFilterValue}
      menuTitle="Filter by environment"
    />
  );
}

const typeFilterOptions = ['RBAC', 'Security', 'Registry', 'Setup'] as const;

function TypeFilter({
  column: { getFilterValue, setFilterValue, id },
}: {
  column: Column<EnrichedPolicy>;
}) {
  const value = getFilterValue();
  const valueAsArray = getValueAsArrayOfStrings(value);

  return (
    <MultipleSelectionFilter
      options={typeFilterOptions}
      filterKey={id}
      value={valueAsArray}
      onChange={setFilterValue}
      menuTitle="Filter by type"
    />
  );
}
